/**
 * Payment-method modal context object, event name, and `usePaymentMethodModal` hook.
 *
 * `.ts` (no JSX) so `PaymentMethodModalProvider.tsx` stays a Fast Refresh component boundary.
 */

import { createContext, useContext } from 'react';

/** Custom event name dispatched by the apiClient 402 interceptor for missing card. */
export const PAYMENT_METHOD_REQUIRED_EVENT = 'paymentMethod:required';

export interface PaymentMethodModalContextValue {
  /** Whether the add-payment-method modal is currently open. */
  paymentMethodOpened: boolean;
  /** Imperatively open the modal with an optional reason message. */
  openPaymentMethod: (reason?: string) => void;
  /**
   * Register a one-shot callback to run when the payment-method modal closes.
   * Returns an unregister function for cleanup.
   */
  registerOnClose: (cb: () => void) => () => void;
}

/**
 * React context object. Exported for `PaymentMethodModalProvider` only —
 * consumers use `usePaymentMethodModal`.
 */
export const PaymentMethodModalContext = createContext<PaymentMethodModalContextValue | null>(null);

/**
 * Hook to access the global payment-method modal.
 * Must be used within a PaymentMethodModalProvider.
 */
export const usePaymentMethodModal = (): PaymentMethodModalContextValue => {
  const ctx = useContext(PaymentMethodModalContext);
  if (!ctx) {
    throw new Error('usePaymentMethodModal must be used within a PaymentMethodModalProvider');
  }
  return ctx;
};
